const SECTION_ID = 'wishlist-product-presentation';
const CARD_SELECTOR = '.Vtl-WishlistProduct';
const LINK_SELECTOR = '.Vtl-WishlistProduct__Link';
const TITLE_SELECTOR = '.Vtl-WishlistProduct__Title';
const VARIANT_SELECTOR = '.Vtl-WishlistProduct__Variant';
const IDENTITY_SELECTOR = '[data-wishlist-identity]';
const CLOSE_SELECTOR = ':scope > .Vtl-WishlistProduct__CloseButton, :scope > .Vtl-CloseButton';
const REMOVE_LABEL_SELECTOR = '.wishlist-remove-label';

/**
 * @returns {{ remove: string, removeItem: string }}
 */
function removeLabels() {
  const source = document.getElementById('wishlist-page-labels');
  const fallback = { remove: 'Remove', removeItem: 'Remove __PRODUCT__' };

  if (!(source instanceof HTMLScriptElement) || !source.textContent) return fallback;

  try {
    const labels = JSON.parse(source.textContent);
    return {
      remove: typeof labels.remove === 'string' && labels.remove ? labels.remove : fallback.remove,
      removeItem: typeof labels.removeItem === 'string' && labels.removeItem ? labels.removeItem : fallback.removeItem,
    };
  } catch {
    return fallback;
  }
}

/**
 * @param {string} template
 * @param {string} productName
 * @returns {string}
 */
function accessibleRemoveName(template, productName) {
  return template.replace('__PRODUCT__', productName);
}

/** @type {Map<string, Promise<HTMLElement | null>>} */
const presentationCache = new Map();

/**
 * @param {string} href
 * @returns {string | null}
 */
function presentationKey(href) {
  try {
    const url = new URL(href, window.location.origin);
    if (!url.pathname.includes('/products/')) return null;
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

/**
 * @param {string} href
 * @returns {Promise<HTMLElement | null>}
 */
function loadPresentation(href) {
  const key = presentationKey(href);
  if (!key) return Promise.resolve(null);

  const pending = presentationCache.get(key);
  if (pending) return pending;

  const request = fetchPresentation(href).catch(() => null);
  presentationCache.set(key, request);
  return request;
}

/**
 * @param {string} href
 * @returns {Promise<HTMLElement | null>}
 */
async function fetchPresentation(href) {
  const url = new URL(href, window.location.origin);
  url.searchParams.set('section_id', SECTION_ID);
  url.hash = '';

  const response = await fetch(url);
  if (!response.ok) return null;

  const html = new DOMParser().parseFromString(await response.text(), 'text/html');
  const presentation = html.querySelector('.wishlist-product-presentation');
  return presentation instanceof HTMLElement ? presentation : null;
}

/**
 * The app link is the concrete card instance. An in-place re-render replaces
 * that link, so the replacement is enhanced again; this link is not.
 * @param {HTMLAnchorElement} link
 * @returns {boolean}
 */
function isPresentedLink(link) {
  return link.dataset.wishlistPresented === 'true';
}

/**
 * @param {Element} node
 * @returns {boolean}
 */
function isAdapterNode(node) {
  return (
    node.matches('[data-wishlist-identity], .wishlist-product-identity__code, .wishlist-product-identity__name, .wishlist-remove-label') ||
    Boolean(node.closest('[data-wishlist-identity], .wishlist-remove-label'))
  );
}

/**
 * @param {Element} card
 */
async function presentCard(card) {
  if (!(card instanceof HTMLElement)) return;

  const link = card.querySelector(LINK_SELECTOR);
  if (!(link instanceof HTMLAnchorElement) || !link.getAttribute('href') || isPresentedLink(link)) return;

  const originalHref = link.getAttribute('href');
  link.dataset.wishlistPresented = 'true';
  card.setAttribute('data-wishlist-presented', '');
  presentRemoveAction(card, card.querySelector(TITLE_SELECTOR)?.textContent?.trim() ?? '');

  const presentation = await loadPresentation(link.href);
  if (!presentation || !link.isConnected || link.getAttribute('href') !== originalHref) return;

  const code = presentation.querySelector('.wishlist-product-presentation__code')?.textContent?.trim() ?? '';
  const name = presentation.querySelector('.wishlist-product-presentation__name')?.textContent?.trim() ?? '';
  if (!code && !name) return;

  let identity = card.querySelector(IDENTITY_SELECTOR);
  if (!(identity instanceof HTMLElement)) {
    identity = document.createElement('div');
    identity.className = 'wishlist-product-identity';
    identity.setAttribute('data-wishlist-identity', '');
    link.append(identity);
  }

  identity.replaceChildren();

  if (code) {
    const codeElement = document.createElement('p');
    codeElement.className = 'wishlist-product-identity__code';
    codeElement.textContent = code;
    identity.append(codeElement);
  }

  if (name) {
    const nameElement = document.createElement('p');
    nameElement.className = 'wishlist-product-identity__name';
    nameElement.textContent = name;
    identity.append(nameElement);
  }

  card.querySelector(TITLE_SELECTOR)?.setAttribute('hidden', '');
  card.querySelector(VARIANT_SELECTOR)?.setAttribute('hidden', '');
  presentRemoveAction(card, name);
}

/**
 * @param {HTMLElement} card
 * @param {string} productName
 */
function presentRemoveAction(card, productName) {
  const close = card.querySelector(CLOSE_SELECTOR);
  if (!(close instanceof HTMLElement)) return;

  const labels = removeLabels();
  const visibleLabel = labels.remove;
  const accessibleName = productName ? accessibleRemoveName(labels.removeItem, productName) : visibleLabel;

  if (close instanceof HTMLButtonElement) {
    if (!close.type) close.type = 'button';
  } else if (close.getAttribute('role') !== 'button') {
    close.setAttribute('role', 'button');
  }

  close.tabIndex = 0;

  let label = close.querySelector(REMOVE_LABEL_SELECTOR);
  if (!(label instanceof HTMLElement)) {
    label = document.createElement('span');
    label.className = 'wishlist-remove-label';
    label.setAttribute('aria-hidden', 'true');
    close.append(label);
  }

  label.textContent = visibleLabel;
  close.setAttribute('aria-label', accessibleName);

  if (close.dataset.wishlistRemoveBound === 'true') return;

  close.dataset.wishlistRemoveBound = 'true';
  close.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (event.key === ' ' || event.key === 'Spacebar') event.preventDefault();
    if (event.repeat) return;

    close.click();
  });
}

/**
 * @param {ParentNode} root
 */
function presentCards(root) {
  if (root instanceof Element && root.matches(CARD_SELECTOR)) presentCard(root);

  root.querySelectorAll(CARD_SELECTOR).forEach((card) => {
    presentCard(card);
  });
}

/**
 * A close control can arrive after the card. Re-applying the remove action is
 * idempotent and does not start another presentation request.
 * @param {Element} node
 */
function presentLateRemoveAction(node) {
  const closes = [];
  if (node.matches(CLOSE_SELECTOR)) closes.push(node);
  node.querySelectorAll(CLOSE_SELECTOR).forEach((close) => closes.push(close));

  for (const close of closes) {
    const card = close.closest(CARD_SELECTOR);
    if (!(card instanceof HTMLElement)) continue;

    const link = card.querySelector(LINK_SELECTOR);
    if (!(link instanceof HTMLAnchorElement) || !isPresentedLink(link)) continue;

    const productName =
      card.querySelector('.wishlist-product-identity__name')?.textContent?.trim() ||
      card.querySelector(TITLE_SELECTOR)?.textContent?.trim() ||
      '';
    presentRemoveAction(card, productName);
  }
}

/**
 * An insertion may be the card, a descendant, or a replacement of the card's
 * contents. The last case leaves the card in place, so the parent card is
 * included. Presentation nodes are ignored so enhancement cannot loop.
 * @param {MutationRecord[]} mutations
 */
function presentInsertedCards(mutations) {
  /** @type {Set<HTMLElement>} */
  const cards = new Set();

  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element) || isAdapterNode(node)) continue;

      if (node instanceof HTMLElement && node.matches(CARD_SELECTOR)) cards.add(node);
      node.querySelectorAll(CARD_SELECTOR).forEach((card) => {
        if (card instanceof HTMLElement) cards.add(card);
      });

      const parentCard = node.parentElement?.closest(CARD_SELECTOR);
      if (parentCard instanceof HTMLElement) cards.add(parentCard);

      presentLateRemoveAction(node);
    }
  }

  cards.forEach((card) => presentCard(card));
}

function startWishlistPresentation() {
  presentCards(document);

  const observer = new MutationObserver(presentInsertedCards);
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startWishlistPresentation, { once: true });
} else {
  startWishlistPresentation();
}
